import { useFeatureGate, useStatsigClient } from '@statsig/expo-bindings';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Platform, StyleSheet, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import Modal from 'react-native-modal';
import { SafeAreaView } from 'react-native-safe-area-context';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PtfKeyboardAvoidingView from '../components/PtfKeyboardAvoidingView';

import ErrorBoundary from '../components/errorBoundaries/ErrorBoundary';
import PantryDetails from '../components/pantry/PantryDetails';
import PantryFilter from '../components/pantry/PantryFilter';
import PantryList from '../components/pantry/PantryList';
import PantryMap from '../components/pantry/PantryMap';
import PantrySearchBar from '../components/pantry/PantrySearchBar';
import PantrySpinner from '../components/pantry/PantrySpinner';
import { SiteMapPantriesContent } from '../components/pantry/SiteMapPantriesContent';
import Colors from '../constants/Colors';
import Layout from '../constants/Layout';
import TopMenu from '../navigation/TopMenu';
import * as Actions from '../store/Actions';
import { useLocalization } from '../store/utils/useLocalization';
import { getToken } from '../utils/token';

import { AppText } from '../components/AppText';

// The new Sites-endpoint/FreshTrak map+list experience, gated so it can roll out safely -- the
// legacy PantryMap/PantryList/PantryDetails path (used when this is off) is untouched and remains
// the default. Search is wired up against SearchAllSites inside SiteMapPantriesContent -- filter
// stays hidden entirely (not wired up yet) rather than shown with silently-incomplete results.
const SITE_MAP_STATSIG_GATE = 'new_site_map_with_freshtrak_enabled';

function PantriesScreen(props) {
  const { navigation, registration, actions, route, pantry } = props;
  const { locale, translation } = useLocalization();
  const [updatedAt, setUpdatedAt] = useState(0);
  const [selectedPantryId, setSelectedPantryId] = useState(null);
  const [tabView, setTabView] = useState('map');
  const [searchTerm, setSearchTerm] = useState('');
  const { width, height: windowHeight } = useWindowDimensions();
  const selectedPantryDetailsLoaded = selectedPantryId === pantry.pantry?.id; // check if the redux pantry details matches the selected pantry

  // NOTE: Auto-switching to list view disabled - it was stealing focus while typing
  // Users can manually tap the List tab to see search results
  // This provides better UX and doesn't interrupt the search experience

  const labels = {
    map: translation.fragment_main_tabitem_pantrymap,
    list: translation.fragment_main_tabitem_pantrylist,
  };
  const { value: siteMapEnabled } = useFeatureGate(SITE_MAP_STATSIG_GATE);
  // App.js gives StatsigProviderExpo no loadingComponent, so useFeatureGate returns its default
  // (false) until Statsig finishes initializing -- without this, every cold start would briefly
  // mount the legacy PantryMap/PantryList (firing a location request + pantries fetch) and then,
  // once the gate resolves true, swap to SiteMapPantriesContent (which fetches location/sites
  // again): a visible flash plus duplicated startup I/O. Waiting for isLoading here is scoped to
  // just this screen, rather than blocking the whole app's render via App.js's loadingComponent.
  const { isLoading: siteMapGateLoading } = useStatsigClient();
  // Bounds the wait above: Statsig's isLoading only clears once a real network round-trip
  // resolves, not on a cache hit -- unbounded, this screen (hit by every user, not just those
  // under this gate's rollout) would wait on that network call every single load. After this
  // timeout we stop waiting and render with whatever siteMapEnabled currently is (its safe
  // default is false, i.e. the legacy path) -- re-accepting the original double-mount/flash bug,
  // but only for the narrow intersection of a slow network AND a user actually under the gate,
  // rather than paying the wait on every load for everyone.
  const [siteMapGateWaitTimedOut, setSiteMapGateWaitTimedOut] = useState(false);
  useEffect(() => {
    if (!siteMapGateLoading) return undefined;
    const timeoutId = setTimeout(() => setSiteMapGateWaitTimedOut(true), 500);
    return () => clearTimeout(timeoutId);
  }, [siteMapGateLoading]);
  // Search/filter aren't wired up against the new Sites endpoint yet -- ignore a showFilter deep
  // link entirely while the gate is on rather than showing a filter that silently does nothing.
  const showFilter = Boolean(route.params?.showFilter) && !siteMapEnabled;

  // Handle search changes with backend search integration
  const handleSearchChange = async (newSearchTerm) => {
    setSearchTerm(newSearchTerm);

    const token = await getToken();
    const isLoggedIn = Boolean(token);

    if (newSearchTerm.trim().length >= 3) {
      // Trigger backend search for queries with 3+ characters
      actions.searchPantries(newSearchTerm.trim(), {}, isLoggedIn);
    } else if (newSearchTerm.trim().length === 0) {
      // Clear search and return to explore mode
      actions.clearSearch();
    }
    // For 1-2 characters, show current pantries without search
  };

  // Determine data source based on Redux state
  const isSearchMode = pantry.displayMode === 'search';
  const dataSource = isSearchMode ? pantry.searchResults || [] : pantry.pantries || [];

  // Create single search bar instance to share between map and list views
  const searchBar = (
    <PantrySearchBar translation={translation} searchTerm={searchTerm} onSearchChange={handleSearchChange} />
  );

  useEffect(() => {
    async function getData() {
      const token = await getToken();
      actions.getVisits();
      actions.getTefapConfig();
    }
    getData();

    navigation.addListener('state', (navState) => {
      let newId = get(navState, 'data.state.routes.0.params.id');
      if (newId) {
        newId = parseInt(newId, 10);
        if (newId !== selectedPantryId) {
          setSelectedPantryId(newId);
        }
      }
    });
  }, []);

  useEffect(() => {
    // On narrow layouts, gate on: SiteMapPantriesContent is rendered here instead of PantryMap,
    // and it doesn't consume selectedPantryId at all (only the wide layout's shared
    // PantryDetails Modal below does) -- so a shared .../pantries?id=<legacy organization id>
    // link has nothing to select or center on this layout while the gate is on. Skip it (same
    // scope-narrowing as showFilter above) rather than firing a showSinglePantry request whose
    // result nothing here can show.
    if (siteMapEnabled && Layout.usePantryTabs) return undefined;

    async function getSinglePantry() {
      const token = await getToken();
      const isLoggedIn = Boolean(token);
      if (route.params && route.params.id) {
        setSelectedPantryId(parseInt(route.params.id, 10));
        actions.showSinglePantry(route.params.id, isLoggedIn);
      }
    }
    getSinglePantry();
    return undefined;
  }, [route.params?.id, siteMapEnabled]); // route.params.id (and siteMapEnabled, for the narrow-layout skip above)

  // useCallback so identity is stable across renders unrelated to `actions` -- selectPantry feeds
  // into handleSelectLegacyPantry below, which useSiteSelection's handleSelectSite depends on
  // (see the useCallback comment on handleSelectLegacyPantry for why that matters).
  const selectPantry = useCallback(
    async (pantryId) => {
      setSelectedPantryId(pantryId);
      const token = await getToken();
      const isLoggedIn = Boolean(token);

      actions.showSinglePantry(pantryId, isLoggedIn);
    },
    [actions]
  );

  // Wide layouts, gate on: a legacy (non-FreshTrak) site selection hands off here instead of
  // navigating away, so it shows the same PantryDetails Modal (over just the map/list area) that
  // selecting a legacy pantry from the map/list already does -- see SiteMapPantriesContent.
  // useCallback: passed to SiteMapPantriesContent as onSelectLegacyPantry, which useSiteSelection
  // depends on for its own handleSelectSite -- a new function identity every render would
  // invalidate that memoization and, in turn, every marker's React.memo comparison in a dense
  // region, causing map stutter on pan/zoom.
  const handleSelectLegacyPantry = useCallback(
    (organizationId) => {
      selectPantry(parseInt(organizationId, 10));
    },
    [selectPantry]
  );

  // Gated Sites/FreshTrak map+list fallback -- this feature is still stabilizing against a
  // Sites endpoint that's actively changing shape, so an ErrorBoundary here (per CLAUDE.md) keeps
  // a throw from unmounting the whole app to the root ErrorScreen, taking profile/visits/
  // reservations down with it.
  const siteMapErrorFallback = (
    <View style={styles.errorFallbackContainer} testID="site-map-error-fallback">
      <AppText style={styles.errorFallbackTitle} testID="site-map-error-fallback-title">
        {translation.site_map_error_title || 'Something went wrong'}
      </AppText>
      <AppText style={styles.errorFallbackText} testID="site-map-error-fallback-text">
        {translation.site_map_error_message || "We couldn't load the map. Please try again."}
      </AppText>
    </View>
  );

  const getFilterView = () => (
    <PantryFilter pantry={pantry} actions={actions} translation={translation} navigation={navigation} />
  );
  const getMapView = () => (
    <PantryMap
      actions={actions}
      locale={locale}
      navigation={navigation}
      registration={registration}
      selectPantry={selectPantry}
      selectedPantryId={selectedPantryId}
      setSelectedPantryId={setSelectedPantryId}
      targetZipCode={route.params?.zip}
      translation={translation}
      updatedAt={updatedAt}
      searchBar={searchBar}
      dataSource={dataSource}
    />
  );
  const getMapWithSearchView = () => (
    <View style={styles.mapWithSearchContainer}>
      {!showFilter && searchBar}
      {getMapView()}
    </View>
  );
  const getPantryListView = () => (
    <PantryList
      translation={translation}
      locale={locale}
      pantry={pantry}
      actions={actions}
      navigation={navigation}
      updatedAt={updatedAt}
      selectPantry={selectPantry}
      searchBar={searchBar}
      dataSource={dataSource}
    />
  );

  // The narrow (Layout.usePantryTabs) tab content, kept as its own function (rather than a nested
  // ternary in the JSX below) so showFilter/siteMapEnabled/legacy each get an unambiguous branch.
  const getNarrowMainContent = () => {
    if (showFilter) return getFilterView();
    if (siteMapEnabled) {
      return (
        <ErrorBoundary fallback={siteMapErrorFallback}>
          <SiteMapPantriesContent
            navigation={navigation}
            registration={registration}
            route={route}
            tabView={tabView}
          />
        </ErrorBoundary>
      );
    }
    return (
      <>
        <View
          testID="map-container"
          style={[styles.viewContainer, { display: tabView === 'map' ? 'flex' : 'none' }]}
        >
          {getMapView()}
        </View>
        <View
          testID="list-container"
          style={[styles.viewContainer, { display: tabView === 'list' ? 'flex' : 'none' }]}
        >
          {getPantryListView()}
        </View>
      </>
    );
  };

  // useCallback: passed to SiteMapPantriesContent as onDismissLegacyPantry, which
  // useSiteSelection's dismissSelection depends on -- see handleSelectLegacyPantry's comment
  // above for why an unstable identity here matters.
  const goBack = useCallback(() => {
    setSelectedPantryId(null);
  }, []);

  const navigateToProfile = () => {
    actions.closeMustVerify();
    setSelectedPantryId(null);
    actions.getCurrentUser(() => {
      navigation.navigate('a', { screen: 'profile', params: { screen: 'info' } });
    });
  };

  let content = null;
  if (siteMapGateLoading && !siteMapGateWaitTimedOut) {
    // Neither path below is committed to yet -- avoid mounting (and then immediately tearing
    // down) whichever one turns out to be wrong once the gate resolves.
    content = <PantrySpinner translation={translation} showHeader={false} />;
  } else if (Layout.usePantryTabs) {
    content = (
      <View style={styles.columnContainer}>
        {!showFilter && (
          <View style={styles.tabBar}>
            <View style={styles.container}>
              <TouchableOpacity
                style={tabView == 'map' ? styles.activeTab : styles.tab}
                onPress={() => setTabView('map')}
              >
                <AppText style={tabView == 'map' ? styles.activeTabLabel : styles.tabLabel}>
                  {labels.map}
                </AppText>
              </TouchableOpacity>
              <TouchableOpacity
                style={tabView == 'list' ? styles.activeTab : styles.tab}
                onPress={() => setTabView('list')}
              >
                <AppText style={tabView == 'list' ? styles.activeTabLabel : styles.tabLabel}>
                  {labels.list}
                </AppText>
              </TouchableOpacity>
            </View>
          </View>
        )}
        {!showFilter && !siteMapEnabled && searchBar}
        <View style={styles.container}>{getNarrowMainContent()}</View>
      </View>
    );
  } else {
    // Wide layouts: the PantryDetails Modal (over just the map/list area, not the whole screen)
    // is shared between the legacy path and the gated path -- a legacy site selected via either
    // one shows the same Modal, via the same selectedPantryId/selectPantry state.
    const wideMainContent = siteMapEnabled ? (
      <ErrorBoundary fallback={siteMapErrorFallback}>
        <SiteMapPantriesContent
          navigation={navigation}
          registration={registration}
          route={route}
          tabView={tabView}
          onSelectLegacyPantry={handleSelectLegacyPantry}
          onDismissLegacyPantry={goBack}
        />
      </ErrorBoundary>
    ) : (
      <>
        {showFilter ? getFilterView() : getMapWithSearchView()}
        {getPantryListView()}
      </>
    );

    content = (
      <View style={styles.container}>
        {wideMainContent}
        {selectedPantryId && (
          <Modal
            key={`pantry-details-modal`}
            isVisible
            animationOut="slideOutRight"
            animationIn="slideInRight"
            style={[
              styles.modal,
              {
                left: width / 2,
                width: Layout.usePantryTabs ? width : width / 2,
              },
            ]}
            hasBackdrop={false}
            coverScreen={false}
          >
            {!selectedPantryDetailsLoaded ? (
              <PantrySpinner translation={translation} showHeader={false} />
            ) : (
              <PantryDetails
                key={`pantry-details-${selectedPantryId}`}
                pantry={pantry}
                registration={registration}
                translation={translation}
                locale={locale}
                actions={actions}
                navigation={navigation}
                navigateToProfile={navigateToProfile}
                goBack={goBack}
                organizationId={selectedPantryId}
              />
            )}
          </Modal>
        )}
      </View>
    );
  }

  // Prevent Google Maps' non-React-Native divs from overflowing #root on web.
  // '100vh' is more reliable than a JS pixel value from useWindowDimensions here.
  const webScreenMaxHeight = Platform.OS === 'web' ? { maxHeight: '100vh' } : null;

  // On wide web viewports, the pantry list FlatList was expanding to full content height and forcing
  // the map to match, making zoom controls inaccessible. Applying maxHeight constrains the flex
  // container so the map stays fixed-height and the list scrolls independently.
  const keyboardAvoidingViewStyle = !Layout.usePantryTabs
    ? { flex: 1, maxHeight: windowHeight - Layout.topMenuHeight }
    : { flex: 1 };

  return (
    <SafeAreaView style={[styles.view, webScreenMaxHeight]} edges={['top', 'left', 'right']}>
      <TopMenu {...props} page="pantries" translation={translation} />
      <PtfKeyboardAvoidingView style={keyboardAvoidingViewStyle} keyboardVerticalOffset={0}>
        {content}
      </PtfKeyboardAvoidingView>
    </SafeAreaView>
  );
}

PantriesScreen.navigationOptions = {
  header: null,
};

// had to play with margin bottom to get map to show ok
const styles = StyleSheet.create({
  view: {
    color: Colors.primaryColor,
    backgroundColor: Colors.white,
    flex: 1,
    overflow: 'hidden',
    zIndex: 1,
  },
  modal: {
    top: 0,
    right: 0,
    bottom: 0,
    margin: 0,
  },
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  columnContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  viewContainer: {
    flex: 1,
  },
  mapWithSearchContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  tabBar: {
    backgroundColor: Colors.white,
    height: 48,
    width: '100%',
  },
  tab: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  activeTab: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderBottomColor: Colors.primaryColor,
    borderBottomWidth: 6,
  },
  tabLabel: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: Colors.grayText,
  },
  activeTabLabel: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: Colors.primaryColor,
  },
  errorFallbackContainer: {
    flex: 1,
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorFallbackTitle: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
  },
  errorFallbackText: {
    fontSize: 14,
    color: Colors.grayText,
    textAlign: 'center',
    marginTop: 8,
  },
});

PantriesScreen.propTypes = {
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  registration: PropTypes.objectOf(PropTypes.any).isRequired,
  pantry: PropTypes.objectOf(PropTypes.any).isRequired,
  route: PropTypes.objectOf(PropTypes.any),
  navigation: PropTypes.objectOf(PropTypes.any),
};

function mapStateToProps(state) {
  return {
    registration: state.registration,
    pantry: state.pantry,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PantriesScreen);
